/**
 * Календарь устройства.
 *
 * Google, Outlook, Яндекс и рабочий Exchange стекаются на телефоне в один
 * системный календарь. Читать его — значит получить их все сразу: без
 * регистрации приложения, без экрана согласия Google, без токенов, живущих час,
 * и без нашего сервера. Прямое обращение к их API дало бы то же самое ценой
 * пяти интеграций и проверки приложения в каждой.
 *
 * Здесь только перевод: наружу — понятия приложения (день, минуты от полуночи),
 * внутрь — плагин. Сама сверка о плагине не знает и потому проверяется тестами
 * без телефона.
 *
 * ## Чего этот слой намеренно не берёт
 *
 * События на весь день и растянутые на несколько суток пропускаются. В
 * приложении встреча — это отрезок внутри дня, и день рождения коллеги,
 * ставший восьмичасовым блоком, съел бы бюджет дня целиком. Праздничные
 * календари состоят из таких событий сплошь.
 */

import { Capacitor } from '@capacitor/core';
import type { CapacitorCalendarPlugin } from '@ebarooni/capacitor-calendar';

import { MINUTES_IN_DAY, momentAt, toIsoDate } from '@/shared/lib/datetime';
import { record } from '@/shared/lib/diagnostics';

import { type SystemAllDayEvent, toAllDayEvent } from './all-day';
import type { SyncableFields, SystemEvent } from './merge';

export interface SystemCalendarInfo {
  id: string;
  title: string;
  /** В календарь можно писать: подписки и праздники доступны только на чтение. */
  writable: boolean;
}

export type CalendarAccess = 'granted' | 'denied' | 'prompt' | 'unavailable';

/**
 * Доступ к календарю устройства.
 *
 * Отдельный интерфейс, а не прямые вызовы плагина: так сверку можно проверить
 * на поддельном календаре, а браузер получает честную заглушку вместо ошибки.
 */
export interface SystemCalendarPort {
  available(): boolean;
  checkAccess(): Promise<CalendarAccess>;
  requestAccess(): Promise<CalendarAccess>;
  listCalendars(): Promise<SystemCalendarInfo[]>;
  listEvents(fromMs: number, toMs: number, calendarIds: string[]): Promise<SystemEvent[]>;
  /** События на весь день — дни рождения, праздники: только для показа. */
  listAllDayEvents(
    fromMs: number,
    toMs: number,
    calendarIds: string[],
  ): Promise<SystemAllDayEvent[]>;
  createEvent(calendarId: string, fields: SyncableFields): Promise<string>;
  updateEvent(id: string, fields: SyncableFields): Promise<void>;
  deleteEvent(id: string): Promise<void>;
}

/**
 * Событие устройства в наши понятия. `null` — событие нам не подходит.
 *
 * Отсекается то, что не ложится в сетку дня: события на весь день (их читает
 * `toAllDayEvent` — для строки над днём) и всё, что переходит на следующие
 * сутки.
 */
export function toSystemEvent(raw: {
  id: string;
  calendarId: string | null;
  title: string | null;
  description: string | null;
  location: string | null;
  startDate: number;
  endDate: number;
  isAllDay: boolean;
}): SystemEvent | null {
  if (raw.isAllDay) return null;

  const start = new Date(raw.startDate);
  const end = new Date(raw.endDate);
  const date = toIsoDate(start);
  if (toIsoDate(end) !== date && raw.endDate - raw.startDate > 0) {
    const startMin = start.getHours() * 60 + start.getMinutes();
    // Событие через полночь: в сетке дня ему места нет.
    if (startMin + Math.round((raw.endDate - raw.startDate) / 60000) > MINUTES_IN_DAY) return null;
  }

  const startMin = start.getHours() * 60 + start.getMinutes();
  const durationMin = Math.max(5, Math.round((raw.endDate - raw.startDate) / 60000));

  return {
    id: raw.id,
    calendarId: raw.calendarId,
    title: raw.title?.trim() || 'Без названия',
    description: raw.description ?? '',
    location: raw.location ?? '',
    date,
    startMin,
    durationMin,
  };
}

/** Календарь устройства недоступен: браузер, отказ в доступе, нет плагина. */
export const unavailableCalendar: SystemCalendarPort = {
  available: () => false,
  checkAccess: () => Promise.resolve('unavailable'),
  requestAccess: () => Promise.resolve('unavailable'),
  listCalendars: () => Promise.resolve([]),
  listEvents: () => Promise.resolve([]),
  listAllDayEvents: () => Promise.resolve([]),
  createEvent: () => Promise.reject(new Error('Календарь устройства недоступен')),
  updateEvent: () => Promise.reject(new Error('Календарь устройства недоступен')),
  deleteEvent: () => Promise.reject(new Error('Календарь устройства недоступен')),
};

/**
 * Настоящий календарь устройства.
 *
 * Плагин загружается по требованию: в браузере его нативной части нет, и
 * тянуть её в первый пакет незачем.
 */
export const nativeCalendar: SystemCalendarPort = {
  available: () => Capacitor.isNativePlatform(),

  async checkAccess() {
    const plugin = (await load())?.plugin;
    if (!plugin) return 'unavailable';

    return readAccess(await plugin.checkAllPermissions());
  },

  async requestAccess() {
    const plugin = (await load())?.plugin;
    if (!plugin) return 'unavailable';

    /*
      Спрашиваем систему и тут же сверяемся с ней.

      Ответ самого запроса — слово плагина о том, что он сделал; правда о
      доступе лежит в состоянии разрешений. Версии плагина отвечают по-разному,
      а один отказ без объяснения стоил целой функции, которая молча не
      работала. Поэтому запрос — отдельно, проверка — отдельно, и в журнал
      диагностики попадает и то, и другое.
    */
    try {
      const answer = await plugin.requestFullCalendarAccess();
      record('info', 'календарь.запрос', `плагин ответил: ${String(answer?.result)}`);
    } catch (error) {
      record('error', 'календарь.запрос', error);
    }

    const access = await this.checkAccess();
    record('info', 'календарь.доступ', `после запроса: ${access}`);
    return access;
  },

  async listCalendars() {
    const plugin = (await load())?.plugin;
    if (!plugin) return [];

    const { result } = await plugin.listCalendars();
    return result.map((calendar) => ({
      id: calendar.id,
      title: calendar.title,
      // Подписки и праздничные календари правке не поддаются, и предлагать их
      // как место для наших встреч нельзя.
      writable: calendar.allowsContentModifications !== false && calendar.isImmutable !== true,
    }));
  },

  async listEvents(fromMs, toMs, calendarIds) {
    return (await readRange(fromMs, toMs, calendarIds))
      .map(toSystemEvent)
      .filter((event): event is SystemEvent => event !== null);
  },

  async listAllDayEvents(fromMs, toMs, calendarIds) {
    return (await readRange(fromMs, toMs, calendarIds))
      .map(toAllDayEvent)
      .filter((event): event is SystemAllDayEvent => event !== null);
  },

  async createEvent(calendarId, fields) {
    const plugin = (await load())?.plugin;
    if (!plugin) throw new Error('Календарь устройства недоступен');

    const { id } = await plugin.createEvent({
      calendarId,
      title: fields.title,
      description: fields.notes,
      location: fields.location,
      startDate: momentAt(fields.date, fields.startMin),
      endDate: momentAt(fields.date, fields.startMin + fields.durationMin),
    });

    return id;
  },

  async updateEvent(id, fields) {
    const plugin = (await load())?.plugin;
    if (!plugin) throw new Error('Календарь устройства недоступен');

    await plugin.modifyEvent({
      id,
      title: fields.title,
      description: fields.notes,
      location: fields.location,
      startDate: momentAt(fields.date, fields.startMin),
      endDate: momentAt(fields.date, fields.startMin + fields.durationMin),
    });
  },

  async deleteEvent(id) {
    const plugin = (await load())?.plugin;
    if (!plugin) throw new Error('Календарь устройства недоступен');

    await plugin.deleteEvent({ id });
  },
};

/**
 * Плагин или `null`, если приложение работает во вкладке браузера.
 *
 * Плагин отдаётся завёрнутым, и это не украшение. Его интерфейс — посредник,
 * который отвечает на любое обращение, включая `then`. Верни его из
 * асинхронной функции как есть — и движок увидит «нечто с then», попробует его
 * развернуть, вызовет `CapacitorCalendar.then()`, а плагин ответит «не
 * реализовано на android». Весь вызов падал на этом месте: кнопка «Разрешить
 * доступ» не делала ничего, системное окно не открывалось, встречи не
 * приходили.
 */
async function load(): Promise<{ plugin: CapacitorCalendarPlugin } | null> {
  if (!Capacitor.isNativePlatform()) return null;

  const { CapacitorCalendar } = await import('@ebarooni/capacitor-calendar');
  return { plugin: CapacitorCalendar };
}

/** События устройства в окне — только из выбранных календарей. */
async function readRange(fromMs: number, toMs: number, calendarIds: string[]) {
  const plugin = (await load())?.plugin;
  if (!plugin) return [];

  const { result } = await plugin.listEventsInRange({ from: fromMs, to: toMs });
  const wanted = new Set(calendarIds);

  return result.filter((event) => event.calendarId !== null && wanted.has(event.calendarId));
}

/**
 * Состояния разрешений из ответа плагина.
 *
 * Android кладёт их на верхний уровень ответа (`readCalendar`,
 * `writeCalendar`, `readWriteCalendar`), а во вложенный `result` — сырую
 * карту, от которой в JS ничего не остаётся. Типы плагина описывают только
 * вложенный вариант, iOS отвечает по нему же. Приложение читало один `result`
 * — и видело пустоту: человек нажимал «Разрешить», а в ответ получал «система
 * не дала доступ».
 *
 * Поэтому смотрим в оба места и принимаем общий ключ: разрешения календаря в
 * Android живут одной группой, и «разрешить» там одно на чтение и запись.
 */
function readAccess(answer: unknown): CalendarAccess {
  const source = (answer ?? {}) as Record<string, unknown>;
  const nested = (source.result ?? {}) as Record<string, unknown>;
  const states = { ...source, ...nested };

  const both = states.readWriteCalendar;
  return toAccess(states.readCalendar ?? both, states.writeCalendar ?? both);
}

function toAccess(read: unknown, write: unknown): CalendarAccess {
  if (read === 'granted' && write === 'granted') return 'granted';
  if (read === 'denied' || write === 'denied') return 'denied';
  return 'prompt';
}

/** Календарь устройства, если он есть; иначе честная заглушка. */
export function systemCalendar(): SystemCalendarPort {
  return Capacitor.isNativePlatform() ? nativeCalendar : unavailableCalendar;
}
